import type { ReactNode } from "react"

import { ArtlessDevLogo } from "@/components/ArtlessDevLogo"
import { AnalyticsNotice } from "@/components/marketing/AnalyticsNotice"
import { MarketingAnalytics } from "@/components/marketing/MarketingAnalytics"
import { ButtonLink } from "@/components/marketing/elements/button"
import { Main } from "@/components/marketing/elements/main"
import {
  FooterCategory,
  FooterLink,
  FooterWithLinkCategories,
} from "@/components/marketing/sections/footer-with-link-categories"
import {
  NavbarLink,
  NavbarLogo,
  NavbarWithLinksActionsAndCenteredLogo,
} from "@/components/marketing/sections/navbar-with-links-actions-and-centered-logo"
import { useScrollToHash } from "@/hooks/useScrollToHash"
import { primaryCtaHref, primaryCtaLabel } from "@/lib/launch-config"
import { binskeaUrl, bnskProUrl, contactEmailDomain, contactEmailUser, docsUrl, schoolUrl } from "@/lib/site-links"
import { legalOperator } from "@/lib/legal-config"
import { getUmamiConfig, isAnalyticsOptedOut, setAnalyticsOptOut } from "@/lib/umami"

function BrandLogo() {
  return (
    <>
      <ArtlessDevLogo size="lg" className="md:hidden" />
      <ArtlessDevLogo size="xl" className="hidden md:inline-flex" />
    </>
  )
}

function AnalyticsFooterControl() {
  if (!getUmamiConfig()) return null

  if (isAnalyticsOptedOut()) {
    return (
      <FooterLink
        href="#"
        onClick={(event) => {
          event.preventDefault()
          setAnalyticsOptOut(false)
          window.location.reload()
        }}
      >
        Enable analytics
      </FooterLink>
    )
  }

  return (
    <FooterLink
      href="#"
      onClick={(event) => {
        event.preventDefault()
        setAnalyticsOptOut(true)
        window.location.reload()
      }}
    >
      Analytics opt-out
    </FooterLink>
  )
}

export function MinimalMarketingLayout({ children }: { children: ReactNode }) {
  useScrollToHash()

  return (
    <div className="marketing min-h-screen min-w-0 max-w-full overflow-x-clip bg-background font-sans text-foreground">
      <MarketingAnalytics />
      <NavbarWithLinksActionsAndCenteredLogo
        id="navbar"
        links={<NavbarLink href={docsUrl}>Docs</NavbarLink>}
        logo={
          <NavbarLogo href="/">
            <BrandLogo />
          </NavbarLogo>
        }
        actions={<ButtonLink href={primaryCtaHref()}>{primaryCtaLabel()}</ButtonLink>}
      />

      <Main>{children}</Main>

      <FooterWithLinkCategories
        id="footer"
        links={
          <>
            <FooterCategory title="Product">
              <FooterLink href={docsUrl}>Docs</FooterLink>
            </FooterCategory>
            <FooterCategory title="Legal">
              <FooterLink
                href="#"
                onClick={(event) => {
                  event.preventDefault()
                  window.location.href = `mailto:${contactEmailUser}@${contactEmailDomain}`
                }}
              >
                Contact
              </FooterLink>
              <FooterLink href="/legal/privacy">Privacy</FooterLink>
              <FooterLink href="/legal/terms">Terms</FooterLink>
              <AnalyticsFooterControl />
            </FooterCategory>
            <FooterCategory title="Company">
              <FooterLink href={binskeaUrl} target="_blank" rel="noreferrer">
                Binskea
              </FooterLink>
              <FooterLink href={schoolUrl} target="_blank" rel="noreferrer">
                8020.school
              </FooterLink>
              <FooterLink href={bnskProUrl} target="_blank" rel="noreferrer">
                BNSKpro
              </FooterLink>
            </FooterCategory>
          </>
        }
        fineprint={<p>© 2026 {legalOperator.name}</p>}
      />

      <AnalyticsNotice />
    </div>
  )
}
